import { useEffect } from 'react';

import { useInferencePriceStore } from '@/shared/stores/inference-price-store';

export const useInferencePrices = () => {
  const { prices, isLoading, error, fetchPrices, getPrice } = useInferencePriceStore();

  // Auto-fetch prices on mount if not already loaded
  // Stop retrying if there's an error to prevent infinite loop
  useEffect(() => {
    if (!prices && !isLoading && !error) {
      fetchPrices();
    }
  }, [prices, isLoading, error, fetchPrices]);

  return {
    prices,
    isLoading,
    error,
    getPrice,
    refetch: fetchPrices,
  };
};

// Specific hook for getting a single price
export const useInferencePrice = (
  mediaType: 'image' | 'video' | 'audio',
  resolution?: '480p' | '720p' | '1080p',
  duration?: number,
) => {
  const { prices, isLoading, error, fetchPrices, getPrice } = useInferencePriceStore();

  // Auto-fetch prices on mount if not already loaded
  // Stop retrying if there's an error to prevent infinite loop
  useEffect(() => {
    if (!prices && !isLoading && !error) {
      fetchPrices();
    }
  }, [prices, isLoading, error, fetchPrices]);

  const price = getPrice(mediaType, resolution, duration);

  return {
    price,
    isLoading,
    error,
  };
};

// Hook for video pricing: rate per second × duration
// DB stores "with audio" rate. Without audio = half the rate.
export const useVideoPrice = (
  resolution: '480p' | '720p' | '1080p',
  duration: number,
  audio: boolean = true,
) => {
  const { prices, isLoading, error, fetchPrices, getVideoPrice } = useInferencePriceStore();

  useEffect(() => {
    if (!prices && !isLoading && !error) {
      fetchPrices();
    }
  }, [prices, isLoading, error, fetchPrices]);

  const price = getVideoPrice(resolution, duration, audio);

  return {
    price,
    isLoading,
    error,
  };
};
